import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Award } from 'lucide-react';
import gsap from 'gsap';
import { BASE_URL, addToCart, getImageUrl } from '../../apiConfig';
import perfumeImg from '../../assets/f92f3ad9e5a0452775b81a945b7effbc.jpg';
import './ActiveSalePopup.css';

const ActiveSalePopup = () => {
  const [sale, setSale] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const popupRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {

    const fetchActiveSale = async () => {
      try {
        const response = await fetch(`${BASE_URL}Sale/active`);
        if (!response.ok) return;
        const result = await response.json();
        const activeSale = Array.isArray(result.data) ? result.data[0] : null;

        if (activeSale && activeSale.isActive) {
          let isValid = true;
          if (activeSale.endDate) {
            if (new Date(activeSale.endDate) - new Date() <= 0) {
              isValid = false;
            }
          }

          if (isValid) {
            setSale(activeSale);
            setIsVisible(true);

            // Animate entrance from bottom right
            setTimeout(() => {
              if (popupRef.current) {
                gsap.fromTo(popupRef.current,
                  { y: 120, opacity: 0, scale: 0.85 },
                  { y: 0, opacity: 1, scale: 1, duration: 0.85, ease: 'power4.out' }
                );
              }
            }, 600);
          }
        }
      } catch (err) {
        console.error("Error fetching active sale:", err);
      }
    };

    fetchActiveSale();
  }, []);

  // Countdown timer logic
  useEffect(() => {
    if (!sale?.endDate) return;

    const calculateTimeLeft = () => {
      const difference = new Date(sale.endDate) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsVisible(false);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [sale]);

  const handleDismiss = () => {
    if (!popupRef.current) return;
    gsap.to(popupRef.current, {
      y: 120,
      opacity: 0,
      scale: 0.85,
      duration: 0.45,
      ease: 'power2.in',
      onComplete: () => {
        setIsVisible(false);
        // Reappear after 2 minutes (120,000 ms)
        setTimeout(() => {
          // Double check if sale has ended
          if (sale && sale.endDate && new Date(sale.endDate) - new Date() <= 0) {
            return;
          }
          setIsVisible(true);
          setTimeout(() => {
            if (popupRef.current) {
              gsap.fromTo(popupRef.current,
                { y: 120, opacity: 0, scale: 0.85 },
                { y: 0, opacity: 1, scale: 1, duration: 0.85, ease: 'power4.out' }
              );
            }
          }, 100);
        }, 900000); // Reappear after 15 minutes
      }
    });
  };

  const handleExplore = async () => {
    // Look for product in sale.products array first
    let pId = null;
    if (sale.products && sale.products.length > 0) {
      pId = sale.products[0]?._id || sale.products[0]?.id || sale.products[0];
    } else {
      pId = sale.productId || sale.product?._id || sale.product?.id || sale.product;
    }

    // Fallback ID if nothing is attached in admin panel so the button still works
    if (!pId || typeof pId !== 'string' || pId.length < 10) {
      pId = "6a07148a9969d400fe9259f8"; // Standard fallback product ID
    }

    const token = localStorage.getItem('token');
    if (token) {
      // Logged in — add to cart
      try {
        await addToCart(pId, null, 1);
      } catch (err) {
        console.error("Failed to add sale item to cart:", err);
        navigate('/shop');
      }
    } else {
      // Not logged in — go to product page directly
      navigate(`/product/${pId}`);
    }
    handleDismiss();
  };


  if (!isVisible || !sale) return null;

  // Display exact title as entered in admin dashboard
  const displayTitle = sale.title || 'Exclusive Olfactory Privilege';

  // Determine display image dynamically from sale or its products
  let displayImg = perfumeImg;
  if (sale.image) {
    displayImg = getImageUrl(sale.image);
  } else if (sale.imageUrl) {
    displayImg = getImageUrl(sale.imageUrl);
  } else if (sale.products && sale.products.length > 0 && sale.products[0] && typeof sale.products[0] === 'object') {
    const p = sale.products[0];
    if (p.images && p.images.length > 0) displayImg = getImageUrl(p.images[0]);
    else if (p.image) displayImg = getImageUrl(p.image);
  } else if (sale.product && typeof sale.product === 'object') {
    if (sale.product.images && sale.product.images.length > 0) displayImg = getImageUrl(sale.product.images[0]);
    else if (sale.product.image) displayImg = getImageUrl(sale.product.image);
  }

  return (
    <div className="asp-wrapper" ref={popupRef}>
      {/* Close Button */}
      <button className="asp-close-btn" onClick={handleDismiss} title="Dismiss">
        <X size={14} strokeWidth={2.5} />
      </button>

      {/* Left Image Section */}
      <div className="asp-left">
        <div className="asp-badge">
          <Sparkles size={11} color="#0a0a0a" />
          <span>{sale.discountPercentage || 50}% OFF</span>
        </div>
        <img src={displayImg} alt="Luxury Fragrance Sale" className="asp-perfume-img" />
      </div>

      {/* Right Content Section */}
      <div className="asp-right">
        <div className="asp-brand-tag">
          <Award size={10} /> LIMITED TIME ARCHIVE
        </div>
        <h4 className="asp-title">{displayTitle}</h4>
        <p className="asp-subtitle">Immerse yourself in our masterfully curated private sales event.</p>

        {/* Bottom Row: Timer + CTA */}
        <div className="asp-bottom-row">
          <div className="asp-mini-timer">
            <div className="asp-mini-box">
              <span className="asp-mini-num">{String(timeLeft.days).padStart(2, '0')}</span>
              <span className="asp-mini-lbl">D</span>
            </div>
            <span className="asp-mini-sep">:</span>
            <div className="asp-mini-box">
              <span className="asp-mini-num">{String(timeLeft.hours).padStart(2, '0')}</span>
              <span className="asp-mini-lbl">H</span>
            </div>
            <span className="asp-mini-sep">:</span>
            <div className="asp-mini-box">
              <span className="asp-mini-num">{String(timeLeft.minutes).padStart(2, '0')}</span>
              <span className="asp-mini-lbl">M</span>
            </div>
            <span className="asp-mini-sep">:</span>
            <div className="asp-mini-box">
              <span className="asp-mini-num">{String(timeLeft.seconds).padStart(2, '0')}</span>
              <span className="asp-mini-lbl">S</span>
            </div>
          </div>

          <button className="asp-mini-cta" onClick={handleExplore}>
            <span>CLAIM</span>
            <ArrowRight size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveSalePopup;
